// src/lib/push-notifications.ts — DEM-2043
//
// Cliente Web Push do frontend: ativa/desativa a permissão, faz subscribe via o
// Service Worker do vite-plugin-pwa/workbox (já registrado — NÃO criamos SW novo),
// e persiste/remove a subscription no backend (/v1/push/subscriptions, upsert
// idempotente por endpoint).
//
// iOS (16.4+): pushManager só existe quando o PWA está instalado na tela inicial.
// As funções degradam graciosamente (isPushSupported retorna false) — a UI mostra
// a instrução de instalar o PWA.

import { api } from './api';
export type { NotificationPreferences } from './api';

export type PushPermission = 'default' | 'granted' | 'denied' | 'unsupported';

/** Web Push é suportado quando há SW + PushManager + Notification API. */
export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function getPermission(): PushPermission {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission as PushPermission;
}

/**
 * Detecta iOS sem PWA instalado — caso em que Web Push não funciona (restrição
 * conhecida e aceita). standalone display-mode = instalado na tela inicial.
 */
export function isIosNeedsInstall(): boolean {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent;
  const isIos = /iphone|ipad|ipod/i.test(ua);
  const isStandalone =
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (window.navigator as unknown as { standalone?: boolean }).standalone === true;
  return isIos && !isStandalone && !isPushSupported();
}

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const buffer = new ArrayBuffer(raw.length);
  const output = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
}

function describeDevice(): string {
  const ua = window.navigator.userAgent;
  if (/iphone|ipad|ipod/i.test(ua)) return 'iOS';
  if (/android/i.test(ua)) return 'Android';
  if (/macintosh/i.test(ua)) return 'macOS';
  if (/windows/i.test(ua)) return 'Windows';
  return 'Navegador';
}

async function getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration> {
  // vite-plugin-pwa registra o SW automaticamente; .ready resolve quando ativo.
  return navigator.serviceWorker.ready;
}

/**
 * Ativa push: pede permissão, subscreve via PushManager (chave pública VAPID do
 * backend) e persiste no backend. Retorna a permissão final.
 */
export async function enablePush(): Promise<PushPermission> {
  if (!isPushSupported()) return 'unsupported';

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission as PushPermission;

  const { public_key } = await api.getVapidPublicKey();
  const registration = await getServiceWorkerRegistration();

  // Reusa subscription existente se já houver (idempotente).
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(public_key),
    });
  }

  const json = subscription.toJSON() as {
    endpoint: string;
    keys?: { p256dh?: string; auth?: string };
  };
  await api.savePushSubscription({
    endpoint: json.endpoint,
    keys: { p256dh: json.keys?.p256dh ?? '', auth: json.keys?.auth ?? '' },
    device: describeDevice(),
  });

  return 'granted';
}

/** Desativa push: cancela a subscription no browser e remove no backend. */
export async function disablePush(): Promise<void> {
  if (!isPushSupported()) return;
  const registration = await getServiceWorkerRegistration();
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;

  const endpoint = subscription.endpoint;
  await subscription.unsubscribe().catch(() => undefined);
  await api.deletePushSubscription(endpoint).catch(() => undefined);
}

/** True se este device já tem subscription ativa no browser. */
export async function hasActiveSubscription(): Promise<boolean> {
  if (!isPushSupported() || Notification.permission !== 'granted') return false;
  const registration = await getServiceWorkerRegistration();
  const subscription = await registration.pushManager.getSubscription();
  return subscription !== null;
}
